// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useMemo, useState } from 'preact/hooks';
import qrcode from 'qrcode-generator';
import type { IssuedInvite } from '../api.js';
import { formatDate } from '../format.js';

// ── COB-543: claim links replace platform-sent email invites ──────────
//
// A slot is claimed by presenting its bearer token and nothing else — there is
// no invited address to match, and no second factor. That makes the link itself
// the credential, and the SPA is the only place it is ever legible: the server
// stores its SHA-256 only (AR-17), so nothing can reconstruct it afterwards.
//
// Two facts therefore have to travel with every link the SPA shows, on every
// surface that shows one: it is bearer (whoever opens it takes the slot), and it
// is shown once (there is no "show it again"). The two constants below are the
// single source of that copy so the wizard's links screen and the detail page's
// regenerate row cannot drift apart — and the remedy is named in both, because
// "you can never see this again" without "you can always mint a fresh one" reads
// as a trap rather than a control.

/** The plural bearer warning — the create wizard's links screen, where a whole
 *  set of links is handed over at once. */
export const CLAIM_LINK_BEARER_WARNING =
  'These links are shown once and can never be displayed again — copy each one ' +
  'now. Anyone who opens a link can claim that slot, so send each link ' +
  'privately to the person it is for, and never post one where others can see ' +
  'it. If a link is lost or goes astray, generate a fresh link for that slot ' +
  'from the vault page at any time — a fresh link immediately kills the old one.';

/** The singular sibling — the detail page's per-slot regenerate row. Same two
 *  facts, same remedy, one link. */
export const CLAIM_LINK_BEARER_WARNING_ONE =
  'This link is shown once and can never be displayed again — copy it now. ' +
  'Anyone who opens it can claim that slot, so send it privately to the person ' +
  'it is for, and never post it where others can see it. If it is lost or goes ' +
  'astray, generate a fresh link at any time — a fresh link immediately kills ' +
  'this one.';

/** The heading for one slot's card. A label is the initiator's own reminder of
 *  who a link is for, so it leads when present; an unlabelled slot is named by
 *  its position rather than by its `slot_id`, which means nothing to the person
 *  reading it. `index` is zero-based. */
export function claimLinkSlotTitle(
  invite: Pick<IssuedInvite, 'label'>,
  index: number,
): string {
  const label = invite.label?.trim();
  return label ? label : `Unlabelled slot ${index + 1}`;
}

/**
 * One minted claim link: the slot's name, the URL in a read-only field, Copy +
 * QR affordances, and the expiry.
 *
 * The QR is the in-person hand-off — the two people are in the same room and the
 * link never touches a messaging app. It is generated from the URL with
 * `qrcode-generator`'s byte mode at error-correction level 'L', the same idiom
 * (and the same `.vault-qr`-shaped panel) as the deposit-address field; the
 * markup is trusted because it is built here from a string the server just sent,
 * not from user input.
 */
export function ClaimLinkCard({
  invite,
  title,
}: {
  invite: IssuedInvite;
  /** The slot's name — see {@link claimLinkSlotTitle} for the wizard's
   *  positional fallback; the detail page names the slot by its label. */
  title: string;
}) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const svg = useMemo(() => {
    if (!showQr) return '';
    const qr = qrcode(0, 'L');
    qr.addData(invite.claim_url, 'Byte');
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [invite.claim_url, showQr]);

  function handleCopy() {
    navigator.clipboard.writeText(invite.claim_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div class="sv-link-card" data-testid="sv-claim-link-card">
      <div class="sv-link-card-title">{title}</div>
      <input
        class="accept-input sv-link-url mono"
        type="text"
        readOnly
        value={invite.claim_url}
        aria-label={`Claim link for ${title}`}
        onFocus={(e) => (e.target as HTMLInputElement).select()}
      />
      <div class="vault-field-action">
        <button
          type="button"
          class="btn btn-link"
          onClick={handleCopy}
          aria-live="polite"
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <button
          type="button"
          class="btn btn-link"
          onClick={() => setShowQr(!showQr)}
          aria-expanded={showQr}
        >
          {showQr ? 'Hide QR code' : 'Show QR code'}
        </button>
      </div>
      {showQr && (
        <div
          class="sv-link-qr"
          // Built here from the claim URL the server just returned — no user
          // input, no XSS vector. Same treatment as the deposit-address QR.
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
      <p class="sv-link-expiry">
        This link expires on {formatDate(invite.expires_at_unix)}.
      </p>
    </div>
  );
}
